import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ThemeService } from '@wind-log_library/fe-component-library';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../../../../services/language/language.service';
import { KpiAnalyticsScheduleModel } from '../../../../services-api-v2/kpi-follow-up/models/kpi-analytics-schedule.model';
import { skip, takeUntil } from 'rxjs/operators';
import { ReplaySubject } from 'rxjs';
import { TClassBlockNamePipe } from '../../../../pipes/tclass-block-name/tclass-block-name.pipe';
import { DateTimeFormatPreferencePipe } from 'app/shared/pipes/date-time-format-preference/date-time-format-preference.pipe';
import { TimezoneService } from 'app/shared/services/timezone/timezone.service';
import Highcharts from 'highcharts';
import { CS_BAR_CHART_OPTIONS } from 'app/shared/helper/highchart-options';
import { getXDateFormat } from 'app/shared/helper/highcharts.helper';
import { DateTimeFormatService } from 'app/shared/services/date-time-format/date-time-format.service';

const BAR_HEIGHT = 20;

@Component({
    selector: 'app-kpi-planning-analytics-schedule-chart-view',
    templateUrl: './kpi-planning-analytics-schedule-chart-view.component.html',
    styleUrls: ['./kpi-planning-analytics-schedule-chart-view.component.scss'],
    standalone: false
})

export class KpiPlanningAnalyticsScheduleChartViewComponent implements OnInit, OnChanges {
	@Input() data: KpiAnalyticsScheduleModel;
	@Input() labels: string[];
	@Input() unitStepSize = 24;
	@Input() start: number;
	@Input() end: number;
	@Input() view: 'processes' | 'planned_events' = 'planned_events';
	@Input() processStartEnd: number[];

	public chartHeight = 0;

  public chartOptions: Highcharts.Options;
  public highcharts = Highcharts;
  public chartRef: Highcharts.Chart | null = null;

	private unsubscriber = new ReplaySubject();
	previousLabel: string;

	constructor(
    private themeService: ThemeService,
		public languageService: LanguageService,
		private translateService: TranslateService,
		private translate: TranslateService,
		private tClassBlockNamePipe: TClassBlockNamePipe,
		private tz: TimezoneService,
		private dateTimeFormatPreferencePipe: DateTimeFormatPreferencePipe,
    private dateTimeFormatService: DateTimeFormatService
  ) {}

	ngOnInit() {
		this.subscribeOnThemeChange();
		this.subscribeOnLangChange();
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes.data && changes.data.currentValue &&
			(changes.data.currentValue.plannedEvents?.length > 0 ||
				changes.data.currentValue.realEvents?.length > 0 ||
				this.processStartEnd?.length > 0)) {
			this.initData();
		} else {
			this.chartHeight = 0;
		}
	}

  public chartCallback: Highcharts.ChartCallbackFunction = (chart): void => {
    this.chartRef = chart;
  };

	private initData(): void {
		if (this.data) {
      const allEvents = [...JSON.parse(JSON.stringify(this.data.plannedEvents)), ...JSON.parse(JSON.stringify(this.data.realEvents))];
      this.labels = [...new Set(allEvents.map(a => a.assetName).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })))];
			const newChartHeight = this.labels ? (this.labels.length * (BAR_HEIGHT + 15)) + 120 : 0;
			if (this.chartHeight !== newChartHeight) {
        this.chartHeight = newChartHeight;
				this.setBarChartOptions();
			}
		}
	}

  private setBarChartOptions(): void {
    const dateTimeFormatPreferencePipe = this.dateTimeFormatPreferencePipe;
    const translateService = this.translate;
    const plannedEvents = JSON.parse(JSON.stringify(this.data.plannedEvents));
    const realEvents = JSON.parse(JSON.stringify(this.data.realEvents));
    if (realEvents.length > 0) {
      realEvents.forEach(e => e.category = 'TCLASS')
    }
    if (this.processStartEnd && this.processStartEnd.length > 0) {
      plannedEvents.unshift({
        category: this.translate.instant('KPI.CHARTS.PROCESS_ESTIMATED_START_END'),
        endTs: this.processStartEnd[1],
        assetId: '',
        assetName: '',
        startTs: this.processStartEnd[0],
        color: '#516581',
        type: 'REAL'
      })
      this.labels.unshift(this.translate.instant('KPI.CHARTS.PROCESS_ESTIMATED_START_END'));
    }
    const allEvents = [...plannedEvents, ...realEvents];
    const uniqueCategories = [...new Set(allEvents.map(a => a.category).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })))];
    const eventsByCategory = uniqueCategories.map(a => ({
      category: a,
      color: '',
      events: allEvents.filter(e => e.category === a)
    }));
    const series = eventsByCategory.map((a, i) => ({
      type: 'xrange',
      name: a.category,
      category: a.category,
      data: a.events.map(e => ({
          x: a.category === this.translate.instant('KPI.CHARTS.PROCESS_ESTIMATED_START_END') ? this.processStartEnd[0] : this.tz.unApplyTzFromDate(e.startTs).valueOf(),
          x2: a.category === this.translate.instant('KPI.CHARTS.PROCESS_ESTIMATED_START_END') ? this.processStartEnd[1] : this.tz.unApplyTzFromDate(e.endTs).valueOf(),
          y: a.category === this.translate.instant('KPI.CHARTS.PROCESS_ESTIMATED_START_END') ? 0 : this.labels.findIndex(c => c === e.assetName),
          color: a.category === this.translate.instant('KPI.CHARTS.PROCESS_ESTIMATED_START_END') ? '#516581' : this.getBarColor(e.type, e.color),
          assetName: e.assetName,
          eventType: e.type,
          tcu: e.tcu ? this.tClassBlockNamePipe.transform(e.tcu) : null
      }))
    })).sort((a, b) => a.category.localeCompare(b.category, undefined, { numeric: true, sensitivity: 'base' }));
    this.chartOptions = Highcharts.merge(CS_BAR_CHART_OPTIONS, {
      chart: {
        type: 'xrange',
        height: this.chartHeight > 0 ? this.chartHeight > 120 ? this.chartHeight - 40 : this.chartHeight : 0,
        animation: { duration: 0 }
      },
      plotOptions: {
        series: {
          borderRadius: 0,
          borderWidth: 0,
          pointPadding: 0.2,
          groupPadding: 0,
          animation: false,
          grouping: false,
          states: {
            hover: { enabled: false },
            inactive: { enabled: false }
          }
        },
        xrange: {
          pointWidth: 20
        }
      },
      legend: {
        enabled: false,
        reversed: true,
        verticalAlign: 'top',
        itemStyle: {
          color: 'var(--font)'
        }
      },
      xAxis: {
        type: 'datetime',
        min: this.tz.unApplyTzFromDate(this.start).valueOf(),
        max: this.tz.unApplyTzFromDate(this.end).valueOf(),
        tickInterval: this.getXAxisTickInterval(),
        title: { text: null },
        tickColor: 'var(--divider)',
        lineWidth: 1,
        lineColor: 'var(--divider)',
        gridLineWidth: 1,
        gridLineColor: 'var(--divider)',
        dateTimeLabelFormats: {
          hour: getXDateFormat('t', this.dateTimeFormatService),
          day: getXDateFormat('dm', this.dateTimeFormatService)
        }
      },
      yAxis: {
        categories: this.labels,
        reversed: true,
        lineWidth: 1,
        lineColor: 'var(--divider)',
        gridLineWidth: 1,
        gridLineColor: 'var(--divider)',
        title: { text: null }
      },
      tooltip: {
        enabled: true,
        outside: true,
        shared: false,
        followPointer: true,
        hideDelay: 0,
        animation: { duration: 0 },
        useHTML: true,
        formatter: function() {
          const device = this.point?.series?.userOptions?.data[this.point?.index]?.assetName;
          const otherSeriesPoints: any[] = this.point?.series?.chart?.series
            .filter(s => s.name !== this.point?.series?.userOptions?.category)
            .map(s => s.points)
            .flat()
            .filter(p => p.assetName === device && (p.x <= this.point.x && p.x2 >= this.point.x2)) || [];
          return `
            <div class="font-weight-bold" style="margin-bottom: 5px; font-size: 14px">${device}</div>
            <div style="margin-bottom: 10px">
              <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50px; background-color: ${this.point?.eventType === 'REAL' ? this.point?.color : this.point?.color?.pattern?.color}"></span>
              <span class="font-weight-bold" style="margin-right: 10px; margin-bottom: 5px; font-size: 13px">${this.point?.eventType === 'REAL' ? (this.point?.tcu || this.point?.yCategory) : this.point?.series?.userOptions?.category}</span>
            </div>
            <div>
              <span style="margin-bottom: 5px; font-size: 12px"><b>${translateService.instant('KPI.COMMON.FROM')}: </b> ${dateTimeFormatPreferencePipe.transform(this.point.x, 'dmyt')}</span><br>
              <span style="margin-bottom: 5px; font-size: 12px"><b>${translateService.instant('KPI.COMMON.TO')}: </b> ${dateTimeFormatPreferencePipe.transform(this.point.x2, 'dmyt')}</span>
            </div>
            ${otherSeriesPoints.map(p => {
              return `<div style="margin-top: 10px; margin-bottom: 10px">
                <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50px; background-color: ${p?.color?.pattern?.color || p?.color}"></span>
                <span class="font-weight-bold" style="margin-right: 10px; margin-bottom: 5px; font-size: 13px">${p?.series?.userOptions?.category}</span>
              </div>
              <div>
                <span style="margin-bottom: 5px; font-size: 12px"><b>${translateService.instant('KPI.COMMON.FROM')}: </b> ${dateTimeFormatPreferencePipe.transform(p.x, 'dmyt')}</span><br>
                <span style="margin-bottom: 5px; font-size: 12px"><b>${translateService.instant('KPI.COMMON.TO')}: </b> ${dateTimeFormatPreferencePipe.transform(p.x2, 'dmyt')}</span>
              </div>`
            })}
          `
        }
      },
      series: series
    })
  }

  private getXAxisTickInterval(): number {
    const HOUR = 3600 * 1000;
    const DAY = 24 * HOUR;
    const rules = [
      { max: 1 * DAY,  interval: 1 * HOUR },
      { max: 2 * DAY,  interval: 4 * HOUR },
      { max: 5 * DAY,  interval: 6 * HOUR },
      { max: 10 * DAY, interval: 12 * HOUR },
      { max: 31 * DAY, interval: 1 * DAY },
      { max: 60 * DAY, interval: 2 * DAY },
      { max: 180 * DAY, interval: 7 * DAY },
      { max: 365 * DAY, interval: 30 * DAY },
    ];
    const range = this.end - this.start;
    return rules.find(r => range <= r.max)?.interval || 1 * DAY;
  }

  private getBarColor(eventType: 'REAL' | 'PLANNED', color: string): string | Object {
    if (eventType === 'REAL') {
      return color;
    } else {
      return {
        pattern: {
          color: color,
          path: {
            d: 'M 10 0 L 0 10 M 11 9 L 9 11 M 1 -1 L -1 1',
            strokeWidth: 4,
            stroke: color,
          },
          width: 10,
          height: 10,
        },
      }
    }
  }

	private subscribeOnThemeChange(): void {
		this.themeService.getTheme().pipe(
			skip(1),
			takeUntil(this.unsubscriber)
		).subscribe(() => {
			this.initData();
		});
	}

  private subscribeOnLangChange(): void {
    this.translateService.onLangChange.pipe(takeUntil(this.unsubscriber)).subscribe((event: LangChangeEvent) => {
      this.initData();
    });
  }
}
